// src/components/LoadingSpinner.js

import React from 'react';
import './LoadingSpinner.css'; // Optional: Create a CSS file for additional styling

/**
 * LoadingSpinner Component
 * Displays an animated SVG as a loading indicator.
 */
const LoadingSpinner = () => {
  return (
    <div className="loading-spinner-overlay">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 100 150"
        width="300"
        height="450"
      >
        <defs>
          {/* Metallic gradients */}
          <linearGradient id="screwGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#a1a1a1" />
            <stop offset="40%" stopColor="#6b6b6b" />
            <stop offset="100%" stopColor="#4a4a4a" />
          </linearGradient>

          {/* Animated glass gradient */}
          <radialGradient id="glassGradient" cx="60%" cy="40%" r="50%">
            <stop offset="0%" stopColor="#ffffffee">
              <animate
                attributeName="stop-color"
                values="#ffffffee;#fff9c4ee;#ffffffee"
                dur="3s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor="#ffffff88">
              <animate
                attributeName="stop-color"
                values="#ffffff88;#fff59d88;#ffffff88"
                dur="3s"
                repeatCount="indefinite"
              />
            </stop>
          </radialGradient>

          {/* Loading ring gradient */}
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4CAF50" />
            <stop offset="100%" stopColor="#8BC34A" />
          </linearGradient>

          {/* Enhanced filament glow filter */}
          <filter id="filamentGlow">
            <feGaussianBlur stdDeviation="1.5" result="glow" />
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Loading progress ring */}
        <circle
          cx="50"
          cy="55"
          r="32"
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth="2"
          strokeDasharray="0 1000"
          transform="rotate(-90 50 55)"
        >
          <animate
            attributeName="stroke-dasharray"
            from="0 201"
            to="201 0"
            dur="3s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.5;1;0.5"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Main bulb container */}
        <g transform="translate(50 30)">
          {/* Glass bulb with animated gradient */}
          <path
            d="M0 0 a25 35 0 0 1 0 70 a25 35 0 0 1 0 -70"
            fill="url(#glassGradient)"
            stroke="#d1d1d1"
            strokeWidth="0.8"
          >
            <animate
              attributeName="opacity"
              values="0.9;0.95;0.9"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </path>

          {/* Internal stem */}
          <path d="M0 10 L-2 60 L2 60 Z" fill="#d1d1d1" />
        </g>

        {/* Enhanced filament assembly */}
        <g transform="translate(50 55)" filter="url(#filamentGlow)">
          {/* Glowing support wires */}
          <path
            d="M-8 0 L0 -15 L8 0"
            stroke="#ffd700"
            strokeWidth="1.2"
            fill="none"
            strokeLinejoin="round"
          >
            <animate
              attributeName="opacity"
              values="0.7;1;0.7"
              dur="1.2s"
              repeatCount="indefinite"
            />
          </path>

          {/* Vertical support wire */}
          <path
            d="M0 -15 L0 10"
            stroke="#ffd700"
            strokeWidth="1.2"
          >
            <animate
              attributeName="opacity"
              values="0.7;1;0.7"
              dur="1.2s"
              repeatCount="indefinite"
            />
          </path>

          {/* Central glowing filament */}
          <path
            d="M-6 0 L6 0"
            stroke="#ffeb3b"
            strokeWidth="3"
            strokeLinecap="round"
          >
            <animate
              attributeName="stroke-width"
              values="2;3;2"
              dur="0.8s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.9;1;0.9"
              dur="0.8s"
              repeatCount="indefinite"
            />
          </path>
        </g>

        {/* Dynamic processing effects */}
        <g transform="translate(50 55)" opacity="0.7">
          {/* Pulsing core glow */}
          <circle r="20" fill="#fff9c4" opacity="0.3">
            <animate
              attributeName="r"
              values="18;22;18"
              dur="2.5s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0.2;0.4;0.2"
              dur="2.5s"
              repeatCount="indefinite"
            />
          </circle>

          {/* Loading particles */}
          <g transform="scale(0.8)">
            <circle cx="15" cy="0" r="1.5" fill="#4CAF50">
              <animateMotion
                path="M0 0 Q30 30 0 60"
                dur="3s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;1;0"
                dur="3s"
                repeatCount="indefinite"
              />
            </circle>
            <circle cx="-15" cy="0" r="1.5" fill="#8BC34A">
              <animateMotion
                path="M0 0 Q-30 30 0 60"
                dur="3s"
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                values="0;1;0"
                dur="3s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        </g>

        {/* Screw base with subtle animation */}
        <g transform="translate(50 105)">
          <path
            d="M-15 -15 Q-15 5 0 15 Q15 5 15 -15"
            fill="url(#screwGradient)"
          >
            <animate
              attributeName="opacity"
              values="1;0.95;1"
              dur="2s"
              repeatCount="indefinite"
            />
          </path>

          {/* Screw threads */}
          <path
            d="M-14 -5 
               A14 5 0 0 1 14 -5
               A14 5 0 0 1 -14 0
               A14 5 0 0 1 14 0
               A14 5 0 0 1 -14 5
               A14 5 0 0 1 14 5"
            stroke="#4a4a4a"
            strokeWidth="0.6"
            fill="none"
            strokeLinecap="round"
          />
        </g>

        {/* Base contact point with activity */}
        <circle cx="50" cy="115" r="3" fill="#2d2d2d">
          <animate
            attributeName="fill"
            values="#2d2d2d;#4CAF50;#2d2d2d"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    </div>
  );
};

export default LoadingSpinner;
